import serviciosV2Client from "../../../helpers/serviciosV2Client"

const BASE = "folios"

const cleanParams = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  )

export const getResumen = params =>
  serviciosV2Client.get(`${BASE}/resumen`, { params: cleanParams(params) })

export const getSucursales = params =>
  serviciosV2Client.get(`${BASE}/sucursales`, { params: cleanParams(params) })

export const getGaps = params =>
  serviciosV2Client.get(`${BASE}/gaps`, { params: cleanParams(params) })

export const getHistorial = params =>
  serviciosV2Client.get(`${BASE}/historial`, { params: cleanParams(params) })

export const getIncremental = params =>
  serviciosV2Client.get(`${BASE}/incremental`, { params: cleanParams(params) })

/**
 * Descarga CSV directo del backend.
 * Usa responseType blob + axiosClient para mandar el Bearer token
 * y dispara la descarga en el navegador con un link temporal.
 */
export const descargarExportCSV = async ({ nitemisor, prefijo, max = 50000, fecha_desde, fecha_hasta }) => {
  const response = await serviciosV2Client.get(`${BASE}/export`, {
    params: cleanParams({ nitemisor, prefijo, max, fecha_desde, fecha_hasta }),
    responseType: "blob",
  })

  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  const fecha = new Date().toISOString().split("T")[0]
  link.setAttribute("download", `folios_faltantes_${nitemisor}_${prefijo}_${fecha}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Devuelve el CSV (como string) de un solo prefijo sin disparar descarga.
 * Usado por la exportación global para concatenar múltiples prefijos.
 */
const fetchCSVString = async ({ nitemisor, prefijo, max = 50000, fecha_desde, fecha_hasta }) => {
  const response = await serviciosV2Client.get(`${BASE}/export`, {
    params: cleanParams({ nitemisor, prefijo, max, fecha_desde, fecha_hasta }),
    responseType: "text",
    transformResponse: [data => data],
  })
  return response.data
}

/**
 * Descarga un CSV consolidado con todos los gaps de todos los prefijos
 * con gaps. Hace N llamadas en paralelo (limite CONCURRENCIA) al endpoint
 * /export y concatena los resultados agregando columna "sucursales"
 * (lista de sucursales que comparten el prefijo).
 *
 * @param {object} opts
 * @param {string} opts.nitemisor
 * @param {Array} opts.prefijos - resumen.prefijos[]
 * @param {string} [opts.fecha_desde]
 * @param {string} [opts.fecha_hasta]
 * @param {function} [opts.onProgress] - (hechas, total) => void
 * @param {function} [opts.isCancelled] - () => bool, permite cancelar
 * @returns {Promise<{ totalPrefijos, totalLineas, errores }>}
 */
export const descargarExportGlobal = async ({
  nitemisor,
  prefijos,
  fecha_desde,
  fecha_hasta,
  onProgress,
  isCancelled,
}) => {
  const CONCURRENCIA = 4

  const tareas = (prefijos || [])
    .filter(p => (p.gaps_estimados ?? 0) > 0)
    .map(p => ({
      prefijo: p.prefijo,
      sucursales: (p.sucursales_lista || []).join(" + "),
    }))

  const total = tareas.length
  if (total === 0) {
    throw new Error("No hay prefijos con gaps para exportar.")
  }

  const resultados = new Array(total)
  const errores = []
  let completadas = 0
  let siguiente = 0

  const escaparCSV = valor => {
    const s = String(valor ?? "")
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const worker = async () => {
    while (siguiente < total) {
      if (isCancelled && isCancelled()) return
      const idx = siguiente++
      const t = tareas[idx]
      try {
        const csv = await fetchCSVString({
          nitemisor,
          prefijo: t.prefijo,
          fecha_desde,
          fecha_hasta,
        })
        resultados[idx] = { ...t, csv }
      } catch (err) {
        errores.push({ ...t, error: err.response?.data?.message || err.message })
        resultados[idx] = { ...t, csv: "" }
      } finally {
        completadas++
        if (onProgress) onProgress(completadas, total)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCIA, total) }, () => worker()))

  if (isCancelled && isCancelled()) {
    throw new Error("Exportación cancelada.")
  }

  // Consolidar: header único + filas con columna "sucursales"
  const header = "sucursales,prefijo,folio,fecha_estimada\n"
  const lineas = []

  resultados.forEach(r => {
    if (!r.csv) return
    const filas = r.csv.split(/\r?\n/)
    // Primera línea del back es header (prefijo,folio,fecha_estimada) — la saltamos
    for (let i = 1; i < filas.length; i++) {
      const fila = filas[i]
      if (!fila.trim()) continue
      lineas.push(`${escaparCSV(r.sucursales)},${fila}`)
    }
  })

  const contenido = header + lineas.join("\n") + "\n"

  const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  const fecha = new Date().toISOString().split("T")[0]
  link.setAttribute("download", `folios_faltantes_${nitemisor}_TODOS_${fecha}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return { totalPrefijos: total, totalLineas: lineas.length, errores }
}
